import React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Loader,
  RefreshCw,
  X,
} from 'lucide-react';

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled', 'partial_completed']);

const normalizeStatus = (value) => String(value || '').trim().toLowerCase();

const formatStatusLabel = (value, fallback = 'Queued') => {
  const normalized = normalizeStatus(value);
  if (!normalized) return fallback;
  if (normalized === 'in_progress') return 'Generating';
  if (normalized === 'partial_completed') return 'Partially Complete';
  return normalized
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const statusTone = (status) => {
  const normalized = normalizeStatus(status);
  if (normalized === 'completed') {
    return {
      badge: 'border-emerald-200 bg-emerald-50 text-emerald-700',
      icon: 'text-emerald-600',
      Icon: CheckCircle2,
    };
  }
  if (normalized === 'failed' || normalized === 'error') {
    return {
      badge: 'border-rose-200 bg-rose-50 text-rose-700',
      icon: 'text-rose-600',
      Icon: AlertTriangle,
    };
  }
  if (normalized === 'cancelled') {
    return {
      badge: 'border-slate-200 bg-slate-100 text-slate-600',
      icon: 'text-slate-500',
      Icon: X,
    };
  }
  if (normalized === 'running' || normalized === 'in_progress' || normalized === 'processing') {
    return {
      badge: 'border-blue-200 bg-blue-50 text-blue-700',
      icon: 'text-blue-600',
      Icon: Loader,
    };
  }
  return {
    badge: 'border-slate-200 bg-white text-slate-600',
    icon: 'text-slate-500',
    Icon: Clock3,
  };
};

const getItemCaseCount = (item) => {
  const summary = item?.result_summary || {};
  const payload = item?.result_payload || {};
  return Number(summary?.total_count || payload?.test_cases?.total_count || 0);
};

export const GenerationProgressStep = ({
  sections = [],
  selectedSectionNumbers = [],
  bulkJob = null,
  bulkItems = [],
  bulkEvents = [],
  bulkLoading = false,
  bulkError = null,
  onOpenOutput,
  onRetryFailed,
  onSelectSections,
}) => {
  const sectionLookup = React.useMemo(() => {
    const lookup = new Map();
    (Array.isArray(sections) ? sections : []).forEach((section) => {
      lookup.set(Number(section?.number), section);
    });
    return lookup;
  }, [sections]);

  const backendItems = Array.isArray(bulkItems) ? bulkItems : [];
  const fallbackItems = React.useMemo(() => {
    if (backendItems.length > 0) return [];
    return (Array.isArray(selectedSectionNumbers) ? selectedSectionNumbers : [])
      .map((sectionNumber) => {
        const normalizedSectionNumber = Number(sectionNumber);
        if (!Number.isFinite(normalizedSectionNumber) || normalizedSectionNumber <= 0) return null;
        const section = sectionLookup.get(normalizedSectionNumber);
        return {
          id: `pending-section-${normalizedSectionNumber}`,
          section_number: normalizedSectionNumber,
          section_title: section?.title || '',
          status: 'queued',
          _optimistic: true,
        };
      })
      .filter(Boolean);
  }, [backendItems.length, sectionLookup, selectedSectionNumbers]);
  const items = backendItems.length > 0 ? backendItems : fallbackItems;
  const progress = bulkJob?.progress || {};
  const selectedTotal = Array.isArray(selectedSectionNumbers) ? selectedSectionNumbers.length : 0;
  const total = Number(progress?.total || items.length || selectedTotal || 0);
  const terminal = Number(progress?.terminal ?? progress?.current ?? items.filter((item) =>
    TERMINAL_STATUSES.has(normalizeStatus(item?.status))
  ).length);
  const percent = Math.max(
    0,
    Math.min(
      100,
      Number.isFinite(Number(progress?.progress_percent ?? progress?.percent))
        ? Number(progress.progress_percent ?? progress.percent)
        : total > 0
          ? Math.round((terminal / total) * 100)
          : 0
    )
  );
  const jobStatus = normalizeStatus(bulkJob?.status);
  const isTerminal = TERMINAL_STATUSES.has(jobStatus);
  const heading = jobStatus === 'completed'
    ? 'Generated Sections Ready'
    : jobStatus === 'partial_completed'
      ? 'Generation Partially Complete'
      : jobStatus === 'failed'
        ? 'Generation Failed'
        : jobStatus === 'cancelled'
          ? 'Generation Cancelled'
          : 'Generating Selected Sections';
  const hasBackendItems = backendItems.length > 0;
  const completedItems = items.filter((item) => normalizeStatus(item?.status) === 'completed');
  const failedItems = items.filter((item) => ['failed', 'error'].includes(normalizeStatus(item?.status)));
  const enrichmentPending = items.filter((item) => {
    const status = normalizeStatus(item?.result_summary?.enrichment_status);
    return status === 'pending' || status === 'running';
  });
  const latestEvent = Array.isArray(bulkEvents) && bulkEvents.length > 0
    ? bulkEvents[0]?.message || bulkEvents[0]?.event_type
    : '';
  const headerTone = jobStatus === 'completed'
    ? 'border-emerald-200 bg-emerald-50'
    : failedItems.length > 0 || jobStatus === 'failed'
      ? 'border-rose-200 bg-rose-50'
      : 'border-blue-200 bg-blue-50';

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <header className={`border-b px-5 py-5 md:px-6 ${headerTone}`}>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
            Test Case Generation
          </p>
          <h2 className="mt-1 text-2xl font-bold text-slate-900">
            {heading}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
            Track each selected section here and open completed outputs without returning to section selection.
          </p>
        </div>
      </header>

      <div className="space-y-5 p-5 md:p-6">
        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Progress</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{terminal}/{total || '-'}</p>
            <p className="mt-1 text-xs text-slate-500">sections processed</p>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Ready</p>
            <p className="mt-1 text-2xl font-bold text-emerald-800">{completedItems.length}</p>
            <p className="mt-1 text-xs text-emerald-700">outputs available</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</p>
            <p className="mt-1 text-lg font-bold text-slate-900">{formatStatusLabel(jobStatus, bulkLoading ? 'Starting' : 'Waiting')}</p>
            <p className="mt-1 text-xs text-slate-500">
              {latestEvent || (hasBackendItems ? 'Waiting for worker updates.' : 'Creating generation tasks.')}
            </p>
          </div>
        </div>

        <div>
          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${percent}%` }} />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
            <span>{percent}% complete</span>
            <span>{failedItems.length > 0 ? `${failedItems.length} need attention` : isTerminal ? 'Generation finished' : hasBackendItems ? 'Generation queue active' : 'Preparing selected sections'}</span>
          </div>
        </div>

        {bulkError?.startsWith('No generation job was found') && !bulkJob && !bulkLoading && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            <span>No generation progress is linked to this page. Open section selection to start or review a run.</span>
            <button type="button" className="secondary-btn px-3 py-2" onClick={onSelectSections}>View sections</button>
          </div>
        )}
        {bulkError && !(bulkError.startsWith('No generation job was found') && !bulkJob) && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {bulkError}
          </div>
        )}

        {failedItems.length > 0 && isTerminal && (
          <div className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-amber-900">Completed sections are safe</p>
              <p className="mt-0.5 text-xs text-amber-800">Retry only the {failedItems.length} failed section{failedItems.length === 1 ? '' : 's'}; ready artifacts and versions will not be regenerated.</p>
            </div>
            <button type="button" onClick={onRetryFailed} disabled={bulkLoading} className="secondary-btn justify-center px-3 py-2 text-sm disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${bulkLoading ? 'animate-spin' : ''}`} />
              Retry failed sections
            </button>
          </div>
        )}

        {enrichmentPending.length > 0 && (
          <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
            Core test cases are ready for {enrichmentPending.length} section{enrichmentPending.length === 1 ? '' : 's'} while input-set enrichment continues.
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-slate-900">Selected Sections</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {items.length > 0 ? (
              items.map((item) => {
                const status = normalizeStatus(item?.status || 'queued');
                const tone = statusTone(status);
                const StatusIcon = tone.Icon;
                const sectionNumber = Number(item?.section_number);
                const section = sectionLookup.get(sectionNumber);
                const title = item?.section_title || section?.title || 'Untitled section';
                const caseCount = getItemCaseCount(item);
                const canOpen = status === 'completed';

                return (
                  <div key={item?.id || `${sectionNumber}-${title}`} className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-slate-900">
                          Section {Number.isFinite(sectionNumber) ? sectionNumber : '-'} · {title}
                        </p>
                        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${tone.badge}`}>
                          <StatusIcon className={`h-3.5 w-3.5 ${tone.icon} ${status === 'running' || status === 'in_progress' ? 'animate-spin' : ''}`} />
                          {formatStatusLabel(status)}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">
                        {caseCount > 0
                          ? `${caseCount} generated cases`
                          : item?.error?.message || item?.error || (item?._optimistic ? 'Waiting for backend task' : 'Queued for generation')}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onOpenOutput?.(sectionNumber)}
                      disabled={!canOpen}
                      className="secondary-btn justify-center px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {canOpen ? 'Review Test Cases' : 'Waiting'}
                    </button>
                  </div>
                );
              })
            ) : (
              <div className="flex items-center gap-3 px-4 py-5 text-sm text-slate-600">
                {bulkLoading ? (
                  <Loader className="h-4 w-4 animate-spin text-blue-600" />
                ) : (
                  <RefreshCw className="h-4 w-4 text-slate-500" />
                )}
                Preparing generation queue...
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
